"use client"

import { useState } from "react"
import Link from "next/link"
import { CheckCircle2, Loader2, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TurnstileWidget } from "@/components/security/turnstile-widget"

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    setMessage("")
    setLoading(true)

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, turnstileToken }),
      })
      const result = (await response.json()) as {
        error?: string
        message?: string
      }

      if (!response.ok) {
        setError(result.error || "Password recovery is temporarily unavailable.")
        setTurnstileToken("")
        setTurnstileResetKey((key) => key + 1)
        return
      }

      setMessage(
        result.message ||
          "If an active account uses that email, a reset link is on its way.",
      )
    } catch {
      setError("Password recovery is temporarily unavailable.")
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    } finally {
      setLoading(false)
    }
  }

  if (message) {
    return (
      <div className="rounded-[28px] border border-[#d8d0c4] bg-[#fffaf3] p-7 shadow-[0_18px_45px_rgba(77,62,48,0.08)]">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-[#dcead8] text-[#28532c]">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </span>
        <h2 className="mt-5 text-2xl font-black tracking-[-0.04em] text-[#171717]">
          Check your inbox
        </h2>
        <p className="mt-3 leading-7 text-[#655d54]">{message}</p>
        <p className="mt-3 text-sm leading-6 text-[#7b7268]">
          The link expires after one hour and stops working once your password
          changes.
        </p>
        <Link
          href="/login"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full border border-[#171717] bg-[#171717] px-6 text-sm font-black text-white transition-transform hover:-translate-y-0.5"
        >
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-[28px] border border-[#d8d0c4] bg-[#fffaf3] p-6 shadow-[0_18px_45px_rgba(77,62,48,0.08)] sm:p-8"
    >
      {error ? (
        <div
          role="alert"
          className="mb-5 rounded-2xl border border-rose-700/25 bg-rose-50 p-4 text-sm font-medium text-rose-800"
        >
          {error}
        </div>
      ) : null}

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-bold text-[#171717]">
          Account email
        </label>
        <div className="relative">
          <Mail
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7b7268]"
            aria-hidden="true"
          />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="h-12 rounded-2xl pl-11"
            required
          />
        </div>
      </div>

      <div className="mt-5">
        <TurnstileWidget
          onTokenChange={setTurnstileToken}
          resetKey={turnstileResetKey}
          theme="light"
        />
      </div>

      <Button
        type="submit"
        className="mt-5 h-12 w-full rounded-full border border-[#171717] text-base font-black shadow-[inset_0_-3px_0_rgba(23,23,23,0.14)]"
        disabled={loading || !turnstileToken}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            Sending reset link...
          </>
        ) : (
          "Send reset link"
        )}
      </Button>
    </form>
  )
}
